export const EVIDENCE_HEADER_NAME = 'X-Kairos-Evidence-ID';
export const EVIDENCE_ID_BYTES = 16;
export const EVIDENCE_ID_PATTERN = /^[A-Za-z0-9_-]{22,64}$/;
export const EVIDENCE_DELIVERY_SOURCES = Object.freeze({
  NETWORK: 'network',
  DECODED_CACHE: 'decoded-cache',
});
export const EVIDENCE_PHASES = Object.freeze({
  SINGLE_STEP: 'single-step',
  CINE: 'cine',
});
export const EVIDENCE_INTERACTIONS = Object.freeze({
  WHEEL: 'wheel',
  DRAG: 'drag',
  SLIDER: 'slider',
});
export const EVIDENCE_EVENT_TYPES = Object.freeze({
  CREATED: 'created',
  TERMINAL: 'terminal',
});
export const EVIDENCE_TERMINAL_OUTCOMES = Object.freeze([
  'published',
  'preloaded',
  'failed',
  'cancelled',
  'stale',
  'superseded',
]);

const BASE64_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
const CONTEXT_KEYS = Object.freeze([
  'viewport',
  'generation',
  'slice',
  'phase',
  'interaction',
  'temperature',
  'deliverySource',
]);
const VALID_PHASES = new Set(Object.values(EVIDENCE_PHASES));
const VALID_INTERACTIONS = new Set(Object.values(EVIDENCE_INTERACTIONS));
const VALID_TEMPERATURES = new Set(['cold', 'warm']);
const VALID_DELIVERY_SOURCES = new Set(Object.values(EVIDENCE_DELIVERY_SOURCES));
const VALID_TERMINAL_OUTCOMES = new Set(EVIDENCE_TERMINAL_OUTCOMES);
const TRUSTED_IDENTITY_ALLOCATORS = new WeakSet();
const EVIDENCE_CAPABILITY_DESCRIPTORS = new WeakMap();
const REQUEST_ORDINAL_ALLOCATOR_STATES = new WeakMap();

const browserRandomValues = bytes => {
  const cryptoApi = typeof window !== 'undefined' ? window.crypto : null;
  const getRandomValues = cryptoApi?.getRandomValues;
  if (typeof getRandomValues !== 'function') throw new Error('Secure random values are unavailable');
  return Reflect.apply(getRandomValues, cryptoApi, [bytes]);
};

const encodeUnpaddedBase64Url = bytes => {
  let encoded = '';
  for (let offset = 0; offset < bytes.length; offset += 3) {
    const remaining = bytes.length - offset;
    const first = bytes[offset];
    const second = remaining > 1 ? bytes[offset + 1] : 0;
    const third = remaining > 2 ? bytes[offset + 2] : 0;
    const combined = (first << 16) | (second << 8) | third;
    encoded += BASE64_ALPHABET[(combined >>> 18) & 63];
    encoded += BASE64_ALPHABET[(combined >>> 12) & 63];
    if (remaining > 1) encoded += BASE64_ALPHABET[(combined >>> 6) & 63];
    if (remaining > 2) encoded += BASE64_ALPHABET[combined & 63];
  }
  return encoded.replace(/\+/g, '-').replace(/\//g, '_');
};

const createTrackedByteSink = length => {
  const values = new Uint8Array(length);
  const written = new Uint8Array(length);
  const write = (index, value) => {
    if (!Number.isInteger(value) || value < 0 || value > 255) {
      throw new Error('Secure random allocator produced an invalid byte');
    }
    values[index] = value;
    written[index] = 1;
  };
  const sink = {
    length,
    fill(value, start = 0, end = length) {
      for (let index = start; index < end; index += 1) write(index, value);
      return sink;
    },
    set(source, offset = 0) {
      for (let index = 0; index < source.length; index += 1) write(offset + index, source[index]);
    },
  };
  for (let index = 0; index < length; index += 1) {
    Object.defineProperty(sink, index, {
      enumerable: true,
      get: () => values[index],
      set: value => write(index, value),
    });
  }
  return { sink, values, written };
};

const createIdentityAllocator = ({ randomValues, trackWrites }) => {
  const allocatedIdentities = new Set();
  const allocatedOrdinals = new Set();
  const allocator = Object.freeze({
    allocate(identityBytes, allocateEvidenceOrdinal) {
      const evidenceOrdinal = allocateEvidenceOrdinal();
      if (!Number.isSafeInteger(evidenceOrdinal) || evidenceOrdinal <= 0) {
        throw new Error('Evidence ordinal must be a positive safe integer');
      }
      let bytes;
      if (trackWrites) {
        const tracked = createTrackedByteSink(identityBytes);
        randomValues(tracked.sink);
        if (tracked.written.some(value => value !== 1)) {
          throw new Error('Secure random allocator did not fill every byte');
        }
        bytes = tracked.values;
      } else {
        bytes = new Uint8Array(identityBytes);
        randomValues(bytes);
      }
      const identity = encodeUnpaddedBase64Url(bytes);
      if (!EVIDENCE_ID_PATTERN.test(identity)) throw new Error('Evidence identity generation failed');
      if (allocatedIdentities.has(identity)) throw new Error('Duplicate evidence identity');
      if (allocatedOrdinals.has(evidenceOrdinal)) throw new Error('Duplicate evidence ordinal');
      // Both values are validated and collision-checked before either session
      // reservation is committed. Each allocator therefore defines one
      // explicit report run; distinct allocators intentionally define distinct
      // runs and may reuse the same ordinal/raw pair.
      allocatedIdentities.add(identity);
      allocatedOrdinals.add(evidenceOrdinal);
      return Object.freeze({ identity, evidenceOrdinal });
    },
  });
  TRUSTED_IDENTITY_ALLOCATORS.add(allocator);
  return allocator;
};

const productionIdentityAllocator = createIdentityAllocator({
  randomValues: browserRandomValues,
  trackWrites: false,
});

export const createViewportEvidenceIdentityTestAllocator = ({ getRandomValues } = {}) => {
  if (typeof getRandomValues !== 'function') throw new Error('Secure random allocator is required');
  return createIdentityAllocator({ randomValues: getRandomValues, trackWrites: true });
};

const createRequestOrdinalAllocator = state => {
  const allocator = Object.freeze({});
  REQUEST_ORDINAL_ALLOCATOR_STATES.set(allocator, state);
  return allocator;
};

export const createViewportRequestOrdinalAllocator = (options = {}) => {
  let firstOrdinal = 1;
  try {
    if (!options || typeof options !== 'object' || Array.isArray(options)) throw new Error('invalid');
    const keys = Reflect.ownKeys(options);
    if (keys.some(key => key !== 'firstOrdinal')) throw new Error('invalid');
    if (keys.includes('firstOrdinal')) {
      const descriptor = Object.getOwnPropertyDescriptor(options, 'firstOrdinal');
      if (!descriptor || !Object.prototype.hasOwnProperty.call(descriptor, 'value')) throw new Error('invalid');
      firstOrdinal = descriptor.value;
    }
  } catch (_) {
    throw new Error('Request ordinal allocator options are invalid');
  }
  if (!Number.isSafeInteger(firstOrdinal) || firstOrdinal <= 0) {
    throw new Error('Request ordinal must be a positive safe integer');
  }
  return createRequestOrdinalAllocator({ nextOrdinal: firstOrdinal, allocate: null, allocated: new Set() });
};

// Compatibility is deliberately isolated at report-session construction. The
// loader accepts only the resulting branded allocator, never an arbitrary
// per-request callback.
export const createViewportRequestOrdinalAllocatorAdapter = allocateRequestOrdinal => {
  if (typeof allocateRequestOrdinal !== 'function') {
    throw new Error('Request ordinal callback is invalid');
  }
  return createRequestOrdinalAllocator({ nextOrdinal: null, allocate: allocateRequestOrdinal, allocated: new Set() });
};

export const reserveViewportRequestOrdinal = allocator => {
  let state;
  try { state = REQUEST_ORDINAL_ALLOCATOR_STATES.get(allocator); } catch (_) { state = null; }
  if (!state) throw new Error('Request ordinal allocator is invalid');

  let requestOrdinal;
  if (state.allocate) {
    requestOrdinal = state.allocate();
  } else {
    if (state.nextOrdinal === null) throw new Error('Request ordinal allocator is exhausted');
    requestOrdinal = state.nextOrdinal;
  }
  if (!Number.isSafeInteger(requestOrdinal) || requestOrdinal <= 0) {
    throw new Error('Request ordinal must be a positive safe integer');
  }
  if (state.allocated.has(requestOrdinal)) throw new Error('Duplicate request ordinal');

  // Validation and collision checks complete before either reservation state
  // is committed. A callback throw or malformed/duplicate result therefore
  // cannot partially reserve this report-session ordinal.
  state.allocated.add(requestOrdinal);
  if (!state.allocate) {
    state.nextOrdinal = requestOrdinal === Number.MAX_SAFE_INTEGER ? null : requestOrdinal + 1;
  }
  return requestOrdinal;
};

export const isViewportRequestOrdinalAllocator = allocator => {
  try { return REQUEST_ORDINAL_ALLOCATOR_STATES.has(allocator); } catch (_) { return false; }
};

const isSafeNonNegativeInteger = value => Number.isSafeInteger(value) && value >= 0;

const snapshotContext = context => {
  try {
    if (!context || typeof context !== 'object' || Array.isArray(context)) {
      throw new Error('invalid context');
    }
    const keys = Object.keys(context);
    if (keys.length !== CONTEXT_KEYS.length || keys.some(key => !CONTEXT_KEYS.includes(key))) {
      throw new Error('invalid context');
    }
    const snapshot = Object.create(null);
    for (const key of CONTEXT_KEYS) snapshot[key] = context[key];
    return Object.freeze(snapshot);
  } catch (_) {
    throw new Error('Evidence context fields are invalid');
  }
};

const freezeContext = context => {
  const snapshot = snapshotContext(context);
  if (!isSafeNonNegativeInteger(snapshot.viewport)
      || !Number.isSafeInteger(snapshot.generation) || snapshot.generation <= 0
      || !isSafeNonNegativeInteger(snapshot.slice)
      || !VALID_PHASES.has(snapshot.phase)
      || !VALID_INTERACTIONS.has(snapshot.interaction)
      || !VALID_TEMPERATURES.has(snapshot.temperature)) {
    throw new Error('Evidence context fields are invalid');
  }
  if (snapshot.phase === EVIDENCE_PHASES.SINGLE_STEP
      && snapshot.interaction === EVIDENCE_INTERACTIONS.SLIDER) {
    throw new Error('Evidence context fields are invalid');
  }
  if (!VALID_DELIVERY_SOURCES.has(snapshot.deliverySource)) {
    throw new Error('Evidence delivery source is invalid');
  }
  const expectedDeliverySource = snapshot.temperature === 'cold'
    ? EVIDENCE_DELIVERY_SOURCES.NETWORK
    : EVIDENCE_DELIVERY_SOURCES.DECODED_CACHE;
  if (snapshot.deliverySource !== expectedDeliverySource) {
    throw new Error('Evidence temperature and delivery source are inconsistent');
  }
  return Object.freeze(Object.fromEntries(CONTEXT_KEYS.map(key => [key, snapshot[key]])));
};

export const readViewportEvidenceDescriptor = evidence => {
  try { return EVIDENCE_CAPABILITY_DESCRIPTORS.get(evidence) || null; } catch (_) { return null; }
};

export const createViewportEvidenceIdentity = (options = {}) => {
  if (options?.benchmarkMode !== true) return null;
  if (Object.prototype.hasOwnProperty.call(options, 'getRandomValues')) {
    throw new Error('Direct random injection is forbidden');
  }
  const {
    context,
    allocateEvidenceOrdinal,
    identityAllocator = productionIdentityAllocator,
    identityBytes = EVIDENCE_ID_BYTES,
    onEvent,
  } = options;
  if (typeof allocateEvidenceOrdinal !== 'function') throw new Error('Evidence ordinal allocator is required');
  if (!TRUSTED_IDENTITY_ALLOCATORS.has(identityAllocator)) throw new Error('Evidence identity allocator is invalid');
  if (!Number.isInteger(identityBytes) || identityBytes < 16 || identityBytes > 48) {
    throw new Error('Evidence identity byte length is invalid');
  }

  // Context is copied before report-global ordinal allocation and identity work.
  const immutableContext = freezeContext(context);
  const { identity, evidenceOrdinal } = identityAllocator.allocate(identityBytes, allocateEvidenceOrdinal);

  const safeDescriptor = Object.freeze({ evidenceOrdinal, ...immutableContext });
  let terminalOutcome = null;
  const emit = payload => {
    const event = Object.freeze({ evidenceOrdinal, ...immutableContext, ...payload });
    try { onEvent?.(event); } catch (_) {}
    return event;
  };
  emit({ type: EVIDENCE_EVENT_TYPES.CREATED });

  const terminalize = outcome => {
    if (!VALID_TERMINAL_OUTCOMES.has(outcome)) throw new Error('Evidence terminal outcome is invalid');
    if (terminalOutcome !== null) return false;
    terminalOutcome = outcome;
    emit({ type: EVIDENCE_EVENT_TYPES.TERMINAL, outcome });
    return true;
  };

  const authorizePublication = ({ generationCurrent, authorityCurrent } = {}) => {
    if (terminalOutcome !== null) return false;
    if (generationCurrent !== true) {
      terminalize('stale');
      return false;
    }
    if (authorityCurrent !== true) {
      terminalize('superseded');
      return false;
    }
    return true;
  };

  const capability = Object.freeze({
    requestHeader: () => Object.freeze({ name: EVIDENCE_HEADER_NAME, value: identity }),
    matchesResponseEcho: value => typeof value === 'string' && value === identity,
    descriptor: () => safeDescriptor,
    authorizePublication,
    terminalize,
    status: () => Object.freeze({ terminalized: terminalOutcome !== null, outcome: terminalOutcome }),
  });
  EVIDENCE_CAPABILITY_DESCRIPTORS.set(capability, safeDescriptor);
  return capability;
};
